import { BlankSlate } from "@msuite/ui/blank-slate";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Stats } from "@msuite/ui/stats";
import { DataTable } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import { useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { formatInvoiceAmount } from "@/components/incoming-invoices";
import { client } from "@/hono";
import { useIncomingInvoiceOffsetCandidates } from "@/hooks/use-incoming-invoice-offset-candidates";
import { columns } from "./table-config";
import { type PaymentOffsetsFormValues, toOffsetRows, toPicked } from "./to-offset-rows";

/** Props Interface */
interface PaymentOffsetsModalProps extends ModalProps {
	incomingInvoiceId: string;
	number: string | null;
	supplier: string | null;
	/** What the payment comes to after Skonto, before any offset. */
	amount: number;
	/** The outgoing invoices planned so far, in the order they apply. */
	plannedIds: string[];
}

/**
 * Plan the open outgoing invoices the supplier owes that are taken off the invoice's payment, in
 * the order picked. The plan is saved on the invoice, and the export takes it off the transfer.
 */
export function PaymentOffsetsModal({
	isOpen,
	onClose,
	incomingInvoiceId,
	number,
	supplier,
	amount,
	plannedIds,
}: PaymentOffsetsModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<PaymentOffsetsFormValues>({
		defaultValues: { outgoingInvoiceIds: plannedIds },
	});
	const outgoingInvoiceIds = useWatch({ control: methods.control, name: "outgoingInvoiceIds" });

	/** Query */
	const { candidates, isLoading, isError } = useIncomingInvoiceOffsetCandidates(incomingInvoiceId);

	/** Functions */
	async function handleOnSubmit(values: PaymentOffsetsFormValues) {
		const picked = toPicked(candidates, values.outgoingInvoiceIds);
		const response = await client.incoming_invoices[":incomingInvoiceId"]["planned-offsets"].$patch(
			{
				param: { incomingInvoiceId },
				json: { outgoingInvoiceIds: picked.map((candidate) => candidate.id) },
			},
		);
		await responseHandler(response);
		toast.success(t("incoming_invoices.payments.offsets.saved"));
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		onClose();
	}

	/** Computed */
	const summary = useMemo(
		() => toOffsetRows(candidates, outgoingInvoiceIds, amount),
		[candidates, outgoingInvoiceIds, amount],
	);
	const isEmpty = !isLoading && (isError || !candidates.length);

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.payments.offsets.title", { number: guardString(number) })}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				width="900px"
			>
				<Modal.Body>
					<p className="label">
						{t("incoming_invoices.payments.offsets.hint", { supplier: guardString(supplier) })}
					</p>
					{isEmpty ? (
						<BlankSlate
							Icon={icon("ReceiptText")}
							label={t(
								isError
									? "incoming_invoices.payments.offsets.load_failed"
									: "incoming_invoices.payments.offsets.no_candidates",
							)}
						/>
					) : (
						<Modal.TableWrapper>
							<DataTable
								data={summary.rows}
								columns={columns}
								isLoading={isLoading}
								refetch={undefined}
								queryKey={undefined}
								menu={null}
								enableSearch={false}
								persistenceKey="incoming-invoice-offset-candidates-table"
								slots={{
									"below-search": () => (
										<Stats.Container className="card divide-x-[0.5px] *:border-color p-0 *:py-2 *:px-4 gap-0">
											<Stats>
												<Stats.Label>{t("incoming_invoices.payments.offsets.payment")}</Stats.Label>
												<Stats.Value forceNumber={false}>
													{formatInvoiceAmount(amount, "EUR")}
												</Stats.Value>
											</Stats>
											<Stats>
												<Stats.Label>{t("incoming_invoices.payments.offsets.total")}</Stats.Label>
												<Stats.Value forceNumber={false}>
													{formatInvoiceAmount(summary.offsetAmount, "EUR")}
												</Stats.Value>
											</Stats>
											<Stats>
												<Stats.Label>
													{t("incoming_invoices.payments.offsets.to_transfer")}
												</Stats.Label>
												<Stats.Value forceNumber={false}>
													{formatInvoiceAmount(summary.remaining, "EUR")}
												</Stats.Value>
											</Stats>
										</Stats.Container>
									),
								}}
							/>
						</Modal.TableWrapper>
					)}
				</Modal.Body>
				<Modal.FooterForm
					submitLabel={t("incoming_invoices.payments.offsets.apply")}
					isDisabled={summary.hasUnused || isError}
				/>
			</Modal>
		</FormProvider>
	);
}
